"use client";

import type { ReactNode } from "react";

type IconBadgeProps = {
  /** The icon element, already sized (e.g. <RocketIcon size={22} />). */
  children: ReactNode;
  /**
   * 'gradient' = solid gradient swatch + white icon (Feature Action Cards).
   * 'tint' = soft ~10%-opacity tinted swatch + colored icon (Explore grid).
   */
  variant?: "gradient" | "tint";
  /** variant="gradient": a `bg-icon-grad-*` Tailwind class. variant="tint": a hex/CSS color used for both the tint background and the icon color. */
  accent?: string;
  /** sm = 40px / rounded-xl (Explore grid). md = 48px / rounded-2xl (Feature cards). */
  size?: "sm" | "md";
  className?: string;
};

// FIX: client's small-screen reference keeps these badges compact within
// multi-column rows starting at the new `xs` (480px) tier rather than only
// shrinking once stacked - sized back up to the original/desktop values at
// `sm` (640px+, unchanged from the already-verified desktop layout).
const SIZE_CLASS: Record<NonNullable<IconBadgeProps["size"]>, string> = {
  sm: "h-11 w-11 rounded-2xl",
  md: "h-14 w-14 rounded-[1.35rem]",
};

/**
 * Square/rounded icon swatch used throughout the dashboard grids. Shared so
 * the two recurring looks (flat gradient vs. soft tint) stay visually
 * consistent instead of each section hand-rolling its own span + inline style.
 */
export default function IconBadge({ children, variant = "tint", accent, size = "md", className = "" }: IconBadgeProps) {
  const isGradient = variant === "gradient";
  return (
    <span
      className={`flex shrink-0 items-center justify-center border ${SIZE_CLASS[size]} ${isGradient ? `text-white ${accent ?? ""}` : ""} ${className}`}
      style={
        isGradient
          ? {
              borderColor: "rgba(255,255,255,0.12)",
              boxShadow: "0 12px 30px -18px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.16)",
            }
          : accent
            ? {
                backgroundColor: `${accent}14`,
                color: accent,
                borderColor: `${accent}2E`,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
              }
            : undefined
      }
    >
      {children}
    </span>
  );
}
